import React from "react";
import { Store as StoreType } from "../../../__generated__/graphql";
// @ts-ignore - react-helmet-async types may be provided at runtime
import { Helmet } from "react-helmet-async";

type Props = {
  store?: StoreType | null;
};

const StoreSeo: React.FC<Props> = ({ store }) => {
  const siteName = "마감히어로";
  const storeTitle = store?.title || "매장 상세";
  const title = `${storeTitle} | 선착순 특가 할인 받으세요`;

  const descriptionParts: string[] = [];
  if (store?.address) descriptionParts.push(store.address);
  if (store?.detailAddress) descriptionParts.push(store.detailAddress);
  if (store?.closingHours)
    descriptionParts.push(`영업 마감 ${store.closingHours}`);
  if (store?.liveOrderNotice) descriptionParts.push(store.liveOrderNotice);
  if (store?.reviewCount)
    descriptionParts.push(`리뷰 ${store.reviewCount.toLocaleString()}개`);
  const description =
    descriptionParts.join(" · ") ||
    `${siteName}에서 ${storeTitle}의 실시간/예약 구매 정보를 확인하세요.`;

  const imageUrl = store?.img || "/og_image.png";

  const canonical =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";

  const robots =
    store?.isDeleted === true || store?.isHidden === true
      ? "noindex, nofollow, noarchive"
      : "index, follow";

  const sameAs = [store?.instagramUrl, store?.naverPlaceUrl].filter(
    (v): v is string => Boolean(v)
  );

  const jsonLdStore = {
    "@context": "https://schema.org",
    "@type": "Store",
    name: storeTitle,
    url: canonical || undefined,
    image: imageUrl,
    telephone: store?.contactNumber || undefined,
    address:
      store?.address || store?.detailAddress
        ? {
            "@type": "PostalAddress",
            streetAddress: [store?.address, store?.detailAddress]
              .filter(Boolean)
              .join(" "),
          }
        : undefined,
    geo:
      store?.lat && store?.lng
        ? {
            "@type": "GeoCoordinates",
            latitude: store.lat,
            longitude: store.lng,
          }
        : undefined,
    sameAs: sameAs.length > 0 ? sameAs : undefined,
  };

  const jsonLdBreadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "홈",
        item: typeof window !== "undefined" ? `${window.location.origin}/` : "",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: storeTitle,
        item: canonical,
      },
    ],
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="robots" content={robots} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="ko_KR" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={imageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <script type="application/ld+json">{JSON.stringify(jsonLdStore)}</script>
      <script type="application/ld+json">
        {JSON.stringify(jsonLdBreadcrumb)}
      </script>
    </Helmet>
  );
};

export default StoreSeo;
